import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const AttStatus = () => {
  const [attStatuses, setAttStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [formData, setFormData] = useState({
    status_code: '',
    status_name: '',
    att_value: 1.00,
    is_active: true
  });
  const [userPermissions, setUserPermissions] = useState({
    can_view: false,
    can_add: false,
    can_edit: false,
    can_delete: false,
    can_export: false,
    loaded: false
  });

  useAuth(); // Fixed: removed unused 'user' variable
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE;

  const fetchUserPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          can_export: false,
          loaded: true
        });
        return;
      }

      const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.permissions) {
        // Find permissions for Attendance Status module
        const attStatusPermissions = response.data.permissions.find(
          p => p.module_name === 'Attendance Status' || p.module_name === 'AttStatus'
        );

        if (attStatusPermissions) {
          setUserPermissions({
            can_view: Boolean(attStatusPermissions.can_view),
            can_add: Boolean(attStatusPermissions.can_add),
            can_edit: Boolean(attStatusPermissions.can_edit),
            can_delete: Boolean(attStatusPermissions.can_delete),
            can_export: Boolean(attStatusPermissions.can_export),
            loaded: true
          });
        } else {
          // If no attendance status permissions found, set all to false
          setUserPermissions({
            can_view: false,
            can_add: false,
            can_edit: false,
            can_delete: false,
            can_export: false,
            loaded: true
          });
        }
      } else {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          can_export: false,
          loaded: true
        });
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setUserPermissions({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        can_export: false,
        loaded: true
      });
    }
  }, [API_BASE]);

  const fetchAttStatuses = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_att_statuses.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAttStatuses(response.data.statuses);
      }
    } catch (error) {
      setError('Failed to fetch attendance statuses');
      console.error('Error fetching attendance statuses:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    const initializeData = async () => {
      await fetchUserPermissions();
      if (userPermissions.can_view) {
        await fetchAttStatuses();
      }
    };

    initializeData();
  }, [fetchUserPermissions, fetchAttStatuses, userPermissions.can_view]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (editingStatus && !userPermissions.can_edit) {
      alert('You do not have permission to edit attendance statuses');
      return;
    }

    if (!editingStatus && !userPermissions.can_add) {
      alert('You do not have permission to add attendance statuses');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const url = editingStatus
        ? `${API_BASE}/update_att_status.php`
        : `${API_BASE}/add_att_status.php`;

      const response = await axios.post(url,
        editingStatus ? {
          ...formData,
          status_id: editingStatus.status_id,
          att_value: parseFloat(formData.att_value)
        } : { ...formData, att_value: parseFloat(formData.att_value) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        fetchAttStatuses();
        resetForm();
        setShowModal(false);
      } else {
        setError(response.data.error || 'Failed to save attendance status');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save attendance status');
    }
  };

  const handleEdit = (status) => {
    if (!userPermissions.can_edit) {
      alert('You do not have permission to edit attendance statuses');
      return;
    }
    setEditingStatus(status);
    setFormData({
      status_code: status.status_code,
      status_name: status.status_name,
      att_value: status.att_value,
      is_active: status.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (statusId) => {
    if (!userPermissions.can_delete) {
      alert('You do not have permission to delete attendance statuses');
      return;
    }

    if (window.confirm('Are you sure you want to delete this attendance status? This may affect existing attendance records.')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.post(`${API_BASE}/delete_att_status.php`,
          { status_id: statusId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          fetchAttStatuses();
        } else {
          setError(response.data.error || 'Failed to delete attendance status');
        }
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to delete attendance status');
      }
    }
  };

  const handleToggleStatus = async (status) => {
    if (!userPermissions.can_edit) {
      alert('You do not have permission to update attendance statuses');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE}/update_att_status.php`,
        {
          status_id: status.status_id,
          status_code: status.status_code,
          status_name: status.status_name,
          att_value: status.att_value,
          is_active: status.is_active ? 0 : 1
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        fetchAttStatuses();
      }
    } catch (error) {
      setError('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      status_code: '',
      status_name: '',
      att_value: 1.00,
      is_active: true
    });
    setEditingStatus(null);
  };

  const handleModalClose = () => {
    setShowModal(false);
    resetForm();
  };

  // Navigation handlers
  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const goToAttendance = () => {
    navigate('/attendance');
  };

  const goToHome = () => {
    navigate('/');
  };

  // Permission check functions
  const canViewAttStatuses = () => userPermissions.can_view;
  const canAddAttStatus = () => userPermissions.can_add;
  const canEditAttStatus = () => userPermissions.can_edit;
  const canDeleteAttStatus = () => userPermissions.can_delete;

  // Show loading while permissions are being fetched
  if (loading || !userPermissions.loaded) {
    return (
      <div className="d-flex justify-content-center align-items-center youtube-page-container" style={{ height: '50vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading permissions...</p>
        </div>
      </div>
    );
  }

  // If user doesn't have view permission, show access denied
  if (!canViewAttStatuses()) {
    return (
      <div className="container-fluid py-4 youtube-page-container">
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center py-5">
                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                <h3 className="mt-3 text-warning">Access Denied</h3>
                <p className="text-muted">You do not have permission to view attendance statuses.</p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={goToDashboard}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 youtube-page-container">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body py-4">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="btn-group me-3" role="group">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToHome}
                        title="Go to Home"
                      >
                        <i className="bi bi-house"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToDashboard}
                        title="Go to Dashboard"
                      >
                        <i className="bi bi-speedometer2"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToAttendance}
                        title="Go to Attendance"
                      >
                        <i className="bi bi-calendar-check"></i>
                      </button>
                    </div>
                    <div>
                      <h2 className="h4 mb-1 fw-bold text-dark">
                        <i className="bi bi-list-check me-2 text-primary"></i>
                        Attendance Status Management
                        {!canAddAttStatus() && !canEditAttStatus() && !canDeleteAttStatus() && (
                          <span className="badge bg-warning ms-2">View Only</span>
                        )}
                      </h2>
                      <p className="text-muted mb-0">
                        Manage attendance status types and their values
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  {canAddAttStatus() && (
                    <button
                      className="btn btn-primary btn-lg"
                      onClick={() => setShowModal(true)}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Add Status
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-3 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                    onClick={goToHome}
                  >
                    <i className="bi bi-house me-2"></i>
                    Home
                  </button>
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                    onClick={goToDashboard}
                  >
                    <i className="bi bi-speedometer2 me-2"></i>
                    Dashboard
                  </button>
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <button
                    className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                    onClick={goToAttendance}
                  >
                    <i className="bi bi-calendar-check me-2"></i>
                    Attendance
                  </button>
                </div>
                <div className="col-md-3">
                  <Link
                    to="/menu"
                    className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                  >
                    <i className="bi bi-grid-3x3-gap me-2"></i>
                    Menu
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>{error}</div>
              <button
                type="button"
                className="btn-close ms-auto"
                onClick={() => setError('')}
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Status Table */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body p-0">
              {attStatuses.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-list-check display-1 text-muted"></i>
                  <h4 className="mt-3 text-muted">No attendance statuses found</h4>
                  <p className="text-muted">
                    Get started by creating your first attendance status.
                  </p>
                  {canAddAttStatus() && (
                    <button
                      className="btn btn-primary mt-2"
                      onClick={() => setShowModal(true)}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Create First Status
                    </button>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Code</th>
                        <th>Status Name</th>
                        <th>Attendance Value</th>
                        <th>Status</th>
                        <th className="text-end pe-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attStatuses.map((status) => (
                        <tr key={status.status_id}>
                          <td className="ps-4">
                            <span className="badge bg-secondary">{status.status_code}</span>
                          </td>
                          <td>
                            <div className="fw-semibold">{status.status_name}</div>
                            <small className="text-muted">ID: {status.status_id}</small>
                          </td>
                          <td>
                            <span className="badge bg-info text-dark">
                              {status.att_value}
                            </span>
                          </td>
                          <td>
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={status.is_active}
                                onChange={() => handleToggleStatus(status)}
                                disabled={!canEditAttStatus()}
                              />
                              <label className="form-check-label">
                                {status.is_active ? 'Active' : 'Inactive'}
                              </label>
                            </div>
                          </td>
                          <td className="text-end pe-4">
                            <div className="btn-group" role="group">
                              {canEditAttStatus() && (
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleEdit(status)}
                                  title="Edit"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                              )}
                              {canDeleteAttStatus() && (
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDelete(status.status_id)}
                                  title="Delete"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-4">
                  <div className="fw-bold text-primary fs-4">{attStatuses.length}</div>
                  <small className="text-muted">Total Statuses</small>
                </div>
                <div className="col-md-4">
                  <div className="fw-bold text-success fs-4">
                    {attStatuses.filter(s => s.is_active).length}
                  </div>
                  <small className="text-muted">Active Statuses</small>
                </div>
                <div className="col-md-4">
                  <div className="fw-bold text-warning fs-4">
                    {attStatuses.filter(s => !s.is_active).length}
                  </div>
                  <small className="text-muted">Inactive Statuses</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card border-0">
            <div className="card-body text-center py-3">
              <div className="btn-group" role="group">
                <button
                  className="btn btn-outline-primary"
                  onClick={goToHome}
                >
                  <i className="bi bi-house me-2"></i>
                  Back to Home
                </button>
                <button
                  className="btn btn-outline-success"
                  onClick={goToDashboard}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={goToAttendance}
                >
                  <i className="bi bi-calendar-check me-2"></i>
                  Back to Attendance
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="bi bi-list-check me-2"></i>
                  {editingStatus ? 'Edit Attendance Status' : 'Add Attendance Status'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={handleModalClose}
                ></button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Status Code *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="status_code"
                        value={formData.status_code}
                        onChange={handleInputChange}
                        required
                        maxLength="3"
                        placeholder="e.g., P, A, CL"
                      />
                      <div className="form-text">
                        Unique 3-character code
                      </div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Status Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="status_name"
                        value={formData.status_name}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter status name (e.g., Present, Absent)"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label">Attendance Value *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        className="form-control"
                        name="att_value"
                        value={formData.att_value}
                        onChange={handleInputChange}
                        required
                        placeholder="0.00 to 1.00"
                      />
                      <div className="form-text">
                        Enter a value between 0.00 (completely absent) and 1.00 (fully present)
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label">
                          Active Status
                        </label>
                      </div>
                      <div className="form-text">
                        Inactive statuses won't be available for new attendance records
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleModalClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {editingStatus ? 'Update Status' : 'Add Status'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttStatus;